import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useIntersectionObserver } from "@/hooks/useIntersectionObserver";

const SkillsVisualization = () => {
  const [activeCategory, setActiveCategory] = useState("frontend");
  const { elementRef, isIntersecting } = useIntersectionObserver({
    threshold: 0.01,
    rootMargin: "0px 0px -50px 0px",
    triggerOnce: true,
  });

  const skillsData = {
    frontend: [
      { name: "React", level: 95, color: "#61DAFB", category: "Library" },
      { name: "JavaScript", level: 90, color: "#F7DF1E", category: "Language" },
      { name: "TypeScript", level: 85, color: "#3178C6", category: "Language" },
      { name: "Tailwind CSS", level: 90, color: "#06B6D4", category: "Framework" },
      { name: "Next.js", level: 88, color: "#6d28d9", category: "Framework" },
      { name: "Nuxt.js", level: 82, color: "#00DC82", category: "Framework" },
      { name: "Three.js", level: 80, color: "#333333", category: "Library" },
      { name: "Framer Motion", level: 85, color: "#FF0055", category: "Library" },
      { name: "Vite", level: 88, color: "#646CFF", category: "Build Tool" },
    ],
    backend: [
      { name: "Python", level: 92, color: "#3776AB", category: "Language" },
      { name: "FastAPI", level: 88, color: "#009688", category: "Framework" },
      { name: "Django", level: 85, color: "#092E20", category: "Framework" },
      { name: "Node.js", level: 85, color: "#339933", category: "Runtime" },
      { name: "Express.js", level: 83, color: "#444444", category: "Framework" },
      { name: "PostgreSQL", level: 88, color: "#336791", category: "Database" },
      { name: "MongoDB", level: 82, color: "#47A248", category: "Database" },
      { name: "REST APIs", level: 92, color: "#25D366", category: "API" },
    ],
    design: [
      { name: "UI/UX Design", level: 92, color: "#FF6B6B", category: "Design" },
      { name: "Figma", level: 90, color: "#F24E1E", category: "Tool" },
      { name: "Photoshop", level: 88, color: "#31A8FF", category: "Tool" },
      { name: "Premiere Pro", level: 82, color: "#9999FF", category: "Tool" },
      { name: "CapCut", level: 85, color: "#000000", category: "Tool" },
      { name: "After Effects", level: 78, color: "#CF96FD", category: "Tool" },
      { name: "Motion Graphics", level: 82, color: "#96CEB4", category: "Specialty" },
      { name: "Branding", level: 80, color: "#FECA57", category: "Specialty" },
    ],
    tools: [
      { name: "Git/GitHub", level: 93, color: "#F05032", category: "Version Control" },
      { name: "VS Code", level: 95, color: "#007ACC", category: "Editor" },
      { name: "Claude AI", level: 90, color: "#D97757", category: "AI Tool" },
      { name: "ChatGPT", level: 90, color: "#10A37F", category: "AI Tool" },
      { name: "Cloudflare Workers", level: 85, color: "#F38020", category: "Edge" },
      { name: "Cloudflare D1", level: 80, color: "#F38020", category: "Database" },
      { name: "Vercel", level: 88, color: "#333333", category: "Hosting" },
      { name: "Netlify", level: 85, color: "#00C7B7", category: "Hosting" },
      { name: "Docker", level: 82, color: "#2496ED", category: "DevOps" },
      { name: "AWS", level: 75, color: "#FF9900", category: "Cloud" },
      { name: "Linux", level: 83, color: "#FCC624", category: "OS" },
      { name: "CI/CD", level: 80, color: "#2088FF", category: "DevOps" },
    ],
    ai: [
      { name: "Machine Learning", level: 82, color: "#FF6F00", category: "Domain" },
      { name: "OpenAI API", level: 85, color: "#412991", category: "API" },
      { name: "Anthropic API", level: 88, color: "#D97757", category: "API" },
      { name: "NLP", level: 80, color: "#4285F4", category: "Domain" },
      { name: "Data Analysis", level: 83, color: "#E94845", category: "Domain" },
      { name: "TensorFlow", level: 78, color: "#FF6F00", category: "Framework" },
      { name: "AI Automation", level: 88, color: "#10b981", category: "Specialty" },
      { name: "Prompt Engineering", level: 90, color: "#6366f1", category: "Specialty" },
    ],
  };

  const categories = [
    { key: "frontend", label: "Frontend", icon: "" },
    { key: "backend", label: "Backend", icon: "" },
    { key: "design", label: "Design", icon: "" },
    { key: "tools", label: "Tools", icon: "" },
    { key: "ai", label: "AI & ML", icon: "" },
  ];

  if (!skillsData[activeCategory]) {
    return null;
  }

  return (
    <div ref={elementRef} className="w-full">
      {/* Category Tabs */}
      <div className="flex flex-wrap justify-center gap-4 mb-12">
        {categories.map((category) => (
          <motion.button
            key={category.key}
            onClick={() => setActiveCategory(category.key)}
            className={`px-6 py-3 rounded-full font-medium transition-all duration-300 ${
              activeCategory === category.key
                ? "bg-primary-500 text-white shadow-lg"
                : "bg-white dark:bg-dark-900 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-dark-700"
            }`}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.98 }}
          >
            <span className="mr-2">{category.icon}</span>
            {category.label}
          </motion.button>
        ))}
      </div>

      {/* Skills Grid */}
      <motion.div
        key={activeCategory}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
      >
        {skillsData[activeCategory].map((skill, index) => (
          <SkillCard
            key={skill.name}
            skill={skill}
            index={index}
            isVisible={isIntersecting}
          />
        ))}
      </motion.div>

      {/* Radar Chart Visualization */}
      <div className="mt-16 flex justify-center">
        <RadarChart activeCategory={activeCategory} skillsData={skillsData} />
      </div>
    </div>
  );
};

const SkillCard = ({ skill, index, isVisible }) => {
  const [animatedLevel, setAnimatedLevel] = useState(0);

  useEffect(() => {
    if (isVisible) {
      const timer = setTimeout(() => {
        setAnimatedLevel(skill.level);
      }, index * 100);
      return () => clearTimeout(timer);
    }
  }, [isVisible, skill.level, index]);

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: index * 0.1, duration: 0.5 }}
      className="bg-white dark:bg-dark-900 p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 group"
      whileHover={{ y: -5 }}
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <h4 className="font-semibold text-gray-900 dark:text-white">
            {skill.name}
          </h4>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {skill.category}
          </p>
        </div>
        <div className="text-2xl font-bold text-primary-500">
          {animatedLevel}%
        </div>
      </div>

      {/* Progress Bar */}
      <div className="w-full bg-gray-200 dark:bg-dark-700 rounded-full h-3 overflow-hidden">
        <motion.div
          className="h-full rounded-full"
          style={{ backgroundColor: skill.color }}
          initial={{ width: 0 }}
          animate={{ width: isVisible ? `${animatedLevel}%` : 0 }}
          transition={{ duration: 1, delay: index * 0.1, ease: "easeOut" }}
        />
      </div>

      {/* Glow effect on hover */}
      <div
        className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-20 transition-opacity duration-300 pointer-events-none"
        style={{
          boxShadow: `0 0 20px ${skill.color}`,
          background: `linear-gradient(45deg, transparent, ${skill.color}20)`,
        }}
      />
    </motion.div>
  );
};

const RadarChart = ({ activeCategory, skillsData }) => {
  const skills = skillsData[activeCategory];
  const center = 150;
  const radius = 120;
  const levels = 5;

  const angleStep = (2 * Math.PI) / skills.length;

  const getPointPosition = (index, level) => {
    const angle = index * angleStep - Math.PI / 2;
    const distance = (radius * level) / 100;
    return {
      x: center + distance * Math.cos(angle),
      y: center + distance * Math.sin(angle),
    };
  };

  return (
    <div className="relative">
      <svg width="300" height="300" className="overflow-visible">
        {/* Grid circles */}
        {[...Array(levels)].map((_, i) => (
          <circle
            key={i}
            cx={center}
            cy={center}
            r={(radius * (i + 1)) / levels}
            fill="none"
            stroke="currentColor"
            strokeWidth="1"
            className="text-gray-300 dark:text-gray-600"
            opacity="0.3"
          />
        ))}

        {/* Grid lines */}
        {skills.map((_, index) => {
          const angle = index * angleStep - Math.PI / 2;
          const endX = center + radius * Math.cos(angle);
          const endY = center + radius * Math.sin(angle);
          return (
            <line
              key={index}
              x1={center}
              y1={center}
              x2={endX}
              y2={endY}
              stroke="currentColor"
              strokeWidth="1"
              className="text-gray-300 dark:text-gray-600"
              opacity="0.3"
            />
          );
        })}

        {/* Skill polygon */}
        <motion.polygon
          points={skills
            .map((skill, index) => {
              const point = getPointPosition(index, skill.level);
              return `${point.x},${point.y}`;
            })
            .join(" ")}
          fill="rgba(16, 185, 129, 0.2)"
          stroke="#10b981"
          strokeWidth="2"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1, delay: 0.5 }}
        />

        {/* Skill points */}
        {skills.map((skill, index) => {
          const point = getPointPosition(index, skill.level);
          return (
            <motion.circle
              key={skill.name}
              cx={point.x}
              cy={point.y}
              r="4"
              fill={skill.color}
              stroke="white"
              strokeWidth="2"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.3, delay: 0.8 + index * 0.1 }}
            />
          );
        })}

        {/* Skill labels */}
        {skills.map((skill, index) => {
          const labelPoint = getPointPosition(index, 110);
          return (
            <motion.text
              key={`label-${skill.name}`}
              x={labelPoint.x}
              y={labelPoint.y}
              textAnchor="middle"
              className="text-xs font-medium fill-current text-gray-700 dark:text-gray-300"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3, delay: 1 + index * 0.1 }}
            >
              {skill.name}
            </motion.text>
          );
        })}
      </svg>
    </div>
  );
};

export default SkillsVisualization;
